import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../layout/Layout';

interface Section {
  id: number;
  section_id: string;
  title: string;
  content: string;
  position: number;
  subsections: Subsection[];
}

interface Subsection {
  id: number;
  title: string;
  content: string;
  position: number;
}

interface TermsConditionsData {
  title: string;
  last_updated: string;
  version: string;
  sections: Section[];
}

const TermsConditions: React.FC = () => {
  const [termsConditions, setTermsConditions] = useState<TermsConditionsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [showSubsectionModal, setShowSubsectionModal] = useState(false);
  const [editingSection, setEditingSection] = useState<Section | null>(null);
  const [editingSubsection, setEditingSubsection] = useState<Subsection | null>(null);
  const [selectedSectionId, setSelectedSectionId] = useState<number | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    title: '',
    version: '',
    section_id: '',
    section_title: '',
    section_content: '',
    subsection_title: '',
    subsection_content: ''
  });

  useEffect(() => {
    fetchTermsConditions();
  }, []);

  const fetchTermsConditions = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/admin/legal-docs/terms-conditions');
      setTermsConditions(response.data.data.terms_and_conditions);
      setFormData({
        title: response.data.data.terms_and_conditions.title,
        version: response.data.data.terms_and_conditions.version,
        section_id: '',
        section_title: '',
        section_content: '',
        subsection_title: '',
        subsection_content: ''
      });
    } catch (error) {
      console.error('Error fetching terms and conditions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveHeader = async () => {
    try {
      setSaving(true);
      await axios.put('/admin/legal-docs/terms-conditions', {
        title: formData.title,
        version: formData.version
      });
      await fetchTermsConditions();
    } catch (error) {
      console.error('Error saving header:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateSection = async () => {
    try {
      setSaving(true);
      await axios.post('/admin/legal-docs/terms-conditions/sections', {
        section_id: formData.section_id,
        title: formData.section_title,
        content: formData.section_content
      });
      await fetchTermsConditions();
      setShowSectionModal(false);
      resetSectionForm();
    } catch (error) {
      console.error('Error creating section:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSection = async () => {
    if (!editingSection) return;

    try {
      setSaving(true);
      await axios.put(`/admin/legal-docs/terms-conditions/sections/${editingSection.id}`, {
        section_id: formData.section_id,
        title: formData.section_title,
        content: formData.section_content
      });
      await fetchTermsConditions();
      setShowSectionModal(false);
      setEditingSection(null);
      resetSectionForm();
    } catch (error) {
      console.error('Error updating section:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSection = async (sectionId: number) => {
    if (!window.confirm('Are you sure you want to delete this section?')) return;

    try {
      await axios.delete(`/admin/legal-docs/terms-conditions/sections/${sectionId}`);
      await fetchTermsConditions();
    } catch (error) {
      console.error('Error deleting section:', error);
    }
  };

  const handleCreateSubsection = async () => {
    if (!selectedSectionId) return;

    try {
      setSaving(true);
      await axios.post('/admin/legal-docs/terms-conditions/subsections', {
        section_id: selectedSectionId,
        title: formData.subsection_title,
        content: formData.subsection_content
      });
      await fetchTermsConditions();
      setShowSubsectionModal(false);
      resetSubsectionForm();
    } catch (error) {
      console.error('Error creating subsection:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSubsection = async () => {
    if (!editingSubsection) return;

    try {
      setSaving(true);
      await axios.put(`/admin/legal-docs/terms-conditions/subsections/${editingSubsection.id}`, {
        title: formData.subsection_title,
        content: formData.subsection_content
      });
      await fetchTermsConditions();
      setShowSubsectionModal(false);
      setEditingSubsection(null);
      resetSubsectionForm();
    } catch (error) {
      console.error('Error updating subsection:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSubsection = async (subsectionId: number) => {
    if (!window.confirm('Are you sure you want to delete this subsection?')) return;

    try {
      await axios.delete(`/admin/legal-docs/terms-conditions/subsections/${subsectionId}`);
      await fetchTermsConditions();
    } catch (error) {
      console.error('Error deleting subsection:', error);
    }
  };

  const resetSectionForm = () => {
    setFormData(prev => ({
      ...prev,
      section_id: '',
      section_title: '',
      section_content: ''
    }));
  };

  const resetSubsectionForm = () => {
    setFormData(prev => ({
      ...prev,
      subsection_title: '',
      subsection_content: ''
    }));
  };

  const openEditSection = (section: Section) => {
    setEditingSection(section);
    setFormData(prev => ({
      ...prev,
      section_id: section.section_id,
      section_title: section.title,
      section_content: section.content
    }));
    setShowSectionModal(true);
  };

  const openEditSubsection = (subsection: Subsection) => {
    setEditingSubsection(subsection);
    setFormData(prev => ({
      ...prev,
      subsection_title: subsection.title,
      subsection_content: subsection.content
    }));
    setShowSubsectionModal(true);
  };

  const openAddSubsection = (sectionId: number) => {
    setSelectedSectionId(sectionId);
    setEditingSubsection(null);
    resetSubsectionForm();
    setShowSubsectionModal(true);
  };

  if (loading) {
    return (
      <Layout>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <h4 className="mb-sm-0 page-title">Terms & Conditions</h4>
              <div className="page-title-right">
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">Legal Documents</li>
                  <li className="breadcrumb-item active">Terms & Conditions</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            {/* Header Section */}
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">Document Information</h5>
              </div>
              <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Title</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">Version</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.version}
                      onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <label className="form-label">Last Updated</label>
                <input
                  type="text"
                  className="form-control"
                  value={termsConditions?.last_updated ? new Date(termsConditions.last_updated).toLocaleDateString() : ''}
                  disabled
                />
              </div>
              <button
                className="btn btn-primary"
                onClick={handleSaveHeader}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>

            {/* Sections */}
            <div className="card mt-3">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">Sections</h5>
                <button
                  className="btn btn-success btn-sm"
                onClick={() => {
                  setEditingSection(null);
                  resetSectionForm();
                    setShowSectionModal(true);
                  }}
                >
                  <i className="bx bx-plus me-1"></i>
                  Add Section
                </button>
              </div>
              <div className="card-body">
                {termsConditions?.sections.map((section, index) => (
                  <div key={section.id} className="border rounded p-3 mb-3 bg-light">
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <h6 className="mb-2">
                        {index + 1}. {section.title}
                      </h6>
                      <p className="text-muted mb-2">{section.content}</p>
                      
                      {/* Subsections */}
                      {section.subsections && section.subsections.length > 0 && (
                        <div className="ms-3 mt-3">
                          <h6 className="text-muted mb-2 small">Subsections:</h6>
                          {section.subsections.map((subsection, subIndex) => (
                            <div key={subsection.id} className="border-start border-3 border-primary ps-3 mb-2 py-2 bg-white rounded">
                              <div className="d-flex justify-content-between align-items-start">
                                <div className="flex-grow-1">
                                  <strong className="text-muted">
                                    {subIndex + 1}. {subsection.title}
                                  </strong>
                                  <p className="text-muted mb-1">{subsection.content}</p>
                                </div>
                                <div className="btn-group btn-group-sm">
                                  <button
                                    className="btn btn-outline-primary"
                                    onClick={() => openEditSubsection(subsection)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className="btn btn-outline-danger"
                                    onClick={() => handleDeleteSubsection(subsection.id)}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="btn-group btn-group-sm">
                      <button
                        className="btn btn-outline-success"
                        onClick={() => openAddSubsection(section.id)}
                      >
                        <i className="bx bx-plus"></i> Add Subsection
                      </button>
                      <button
                        className="btn btn-outline-primary"
                        onClick={() => openEditSection(section)}
                      >
                        <i className="bx bx-edit"></i> Edit
                      </button>
                      <button
                        className="btn btn-outline-danger"
                        onClick={() => handleDeleteSection(section.id)}
                      >
                        <i className="bx bx-trash"></i> Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

        {/* Section Modal */}
          {showSectionModal && (
            <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="modal-dialog modal-lg">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {editingSection ? 'Edit Section' : 'Add Section'}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => {
                        setShowSectionModal(false);
                        setEditingSection(null);
                        resetSectionForm();
                      }}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Section ID</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.section_id}
                        onChange={(e) => setFormData(prev => ({ ...prev, section_id: e.target.value }))}
                        placeholder="e.g., introduction, definitions"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.section_title}
                        onChange={(e) => setFormData(prev => ({ ...prev, section_title: e.target.value }))}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Content</label>
                      <textarea
                        className="form-control"
                        rows={6}
                        value={formData.section_content}
                        onChange={(e) => setFormData(prev => ({ ...prev, section_content: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowSectionModal(false);
                        setEditingSection(null);
                        resetSectionForm();
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={editingSection ? handleUpdateSection : handleCreateSection}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : (editingSection ? 'Update' : 'Create')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Subsection Modal */}
          {showSubsectionModal && (
            <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="modal-dialog modal-lg">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      {editingSubsection ? 'Edit Subsection' : 'Add Subsection'}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => {
                        setShowSubsectionModal(false);
                        setEditingSubsection(null);
                        setSelectedSectionId(null);
                        resetSubsectionForm();
                      }}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Title</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.subsection_title}
                        onChange={(e) => setFormData(prev => ({ ...prev, subsection_title: e.target.value }))}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Content</label>
                      <textarea
                        className="form-control"
                        rows={6}
                        value={formData.subsection_content}
                        onChange={(e) => setFormData(prev => ({ ...prev, subsection_content: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowSubsectionModal(false);
                        setEditingSubsection(null);
                        setSelectedSectionId(null);
                        resetSubsectionForm();
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={editingSubsection ? handleUpdateSubsection : handleCreateSubsection}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : (editingSubsection ? 'Update' : 'Create')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
      </div>
    </Layout>
  );
};

export default TermsConditions;